import { CheckSquare, Megaphone, Type } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";

interface BuilderClosingPageSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type ClosingPageSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): ClosingPageSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as ClosingPageSettings)
    : {};
}

function getStringSetting(
  settings: ClosingPageSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getBooleanSetting(
  settings: ClosingPageSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: ClosingPageSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderClosingPageSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderClosingPageSettingsProps) {
  const settings = getSettings(page);

  const title = getStringSetting(settings, "title", page.title ?? "Closing Page");
  const summaryHeading = getStringSetting(settings, "summaryHeading", "Next Steps");
  const closingMessage = getStringSetting(settings, "closingMessage");
  const ctaLabel = getStringSetting(settings, "ctaLabel", "Ready to get started");
  const ctaText = getStringSetting(settings, "ctaText");
  const showSummaryBlock = getBooleanSetting(settings, "showSummaryBlock", true);
  const showCallToAction = getBooleanSetting(settings, "showCallToAction", true);
  const showContactBlock = getBooleanSetting(settings, "showContactBlock", true);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-pink-500/10 via-rose-500/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <CheckSquare className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Closing Page Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Adjust the wrap-up message, next-step language, and closing CTA
              that shape the final impression on the stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Closing framing"
          description="Set the visible page title and summary framing for the final page."
        >
          <div>
            <FieldLabel
              label="Page title"
              icon={<Type className="h-4 w-4" />}
              hint="Visible title shown at the top of the closing page."
            />
            <input
              value={title}
              onChange={(e) =>
                onChange(updateSetting(settings, "title", e.target.value))
              }
              disabled={readOnly}
              placeholder="Closing Page"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Summary heading"
              hint="Optional heading for the closing summary or next-step area."
            />
            <input
              value={summaryHeading}
              onChange={(e) =>
                onChange(updateSetting(settings, "summaryHeading", e.target.value))
              }
              disabled={readOnly}
              placeholder="Next Steps"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Closing message"
          description="Set the final summary, reassurance, or wrap-up message shown before the CTA."
        >
          <div>
            <FieldLabel
              label="Closing message"
              hint="Use this to thank the client and reinforce the final takeaway."
            />
            <textarea
              rows={5}
              value={closingMessage}
              onChange={(e) =>
                onChange(updateSetting(settings, "closingMessage", e.target.value))
              }
              disabled={readOnly}
              placeholder="Thank the customer and summarize the final takeaway."
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Call to action"
          description="Control the CTA language that encourages the client to move forward."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <FieldLabel
                label="CTA label"
                icon={<Megaphone className="h-4 w-4" />}
                hint="Short CTA heading or prompt."
              />
              <input
                value={ctaLabel}
                onChange={(e) =>
                  onChange(updateSetting(settings, "ctaLabel", e.target.value))
                }
                disabled={readOnly}
                placeholder="Ready to get started"
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
              />
            </div>

            <div>
              <FieldLabel
                label="CTA text"
                hint="Short supporting line below or beside the CTA heading."
              />
              <input
                value={ctaText}
                onChange={(e) =>
                  onChange(updateSetting(settings, "ctaText", e.target.value))
                }
                disabled={readOnly}
                placeholder="Call, email, or approve to move forward."
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Closing page behavior"
          description="Choose which supporting wrap-up blocks appear on the final page."
        >
          <ToggleField
            label="Show summary block"
            description="Display the final summary or next-step section on the closing page."
            checked={showSummaryBlock}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showSummaryBlock", checked))
            }
          />

          <ToggleField
            label="Show call to action"
            description="Display the CTA area that encourages the customer to move forward."
            checked={showCallToAction}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showCallToAction", checked))
            }
          />

          <ToggleField
            label="Show contact block"
            description="Include company contact details at the bottom of the closing page."
            checked={showContactBlock}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showContactBlock", checked))
            }
          />
        </SectionCard>
      </div>
    </div>
  );
}